import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Info,
  XCircle,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { useProductDetail } from '~/features/explorer/hooks/use-products-catalog';
import { AvailabilityBadge } from '~/features/onboarding/components/availability-badge';
import { ProductDetailGallery } from '~/features/onboarding/components/product-detail-panel/product-detail-gallery';
import {
  AttributesSection,
  DescriptionSection,
  HighlightsSection,
  IdentifiersSection,
  SpecificationsSection,
} from '~/features/onboarding/components/product-detail-panel/product-detail-sections';
import { formatFeedPrice } from '~/features/onboarding/utils/product-detail';
import { CatalogEnrichmentHistory } from '~/features/product-catalog/components/catalog-enrichment-history';
import { toProductDetail } from '~/features/product-catalog/utils/catalog-product-detail';
import { cn } from '~/utils/cn';

type Product = Record<string, unknown>;

interface CatalogDetailPanelProps {
  /** Loose product rows for the current page — index-aligned with the table. */
  products: Product[];
  /** Index of the open product, or `null` when the panel is closed. */
  activeIndex: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  /** Selected connection — needed to fetch the full detail record. */
  connectionId: string;
  /** Catalog connector (e.g. `GOOGLE_MERCHANT`) — picks the enrichment table. */
  connectorCode: string;
  subsidiaryId: string | undefined;
}

type DetailTab = 'details' | 'enrichments';

/** Coerce a loose value to a display string. */
function toStr(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '';
}

/** First numeric value across the given keys (0 when none). */
function num(row: Product, ...keys: string[]): number {
  for (const key of keys) {
    const v = row[key];
    if (v != null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return 0;
}

/** First array-valued key. */
function arr<T>(row: Product, ...keys: string[]): T[] {
  for (const key of keys) {
    const v = row[key];
    if (Array.isArray(v)) return v as T[];
  }
  return [];
}

interface SourceIssue {
  attribute: string;
  code: string;
  description: string;
  severity: string;
}

interface Destination {
  approvedCountries?: string[];
  approved_countries?: string[];
  disapprovedCountries?: string[];
  disapproved_countries?: string[];
  pendingCountries?: string[];
  pending_countries?: string[];
  reportingContext?: string;
  reporting_context?: string;
}

const SEVERITY_STYLES: Record<string, string> = {
  DISAPPROVED: 'border-error/20 bg-error-50 text-error',
  DEMOTED: 'border-warning/20 bg-warning-50 text-warning',
  NOT_IMPACTED: 'border-border bg-muted text-muted-foreground',
};

/** Titled section wrapper — matches the onboarding panel's section chrome so
 *  the extra data-quality sections sit flush with the reused design sections. */
function Section({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: ReactNode;
}) {
  return (
    <section className="border-border border-t px-5 py-4">
      <h3 className="text-foreground mb-3 flex items-center gap-2 text-sm font-semibold">
        {title}
        {count != null && <span className="text-muted-foreground font-normal">({count})</span>}
      </h3>
      {children}
    </section>
  );
}

/** Quality score donut + dimension bars + issue-count summary — read straight
 *  from the row (all scores/counts are present on the list record). */
function QualitySection({ product }: { product: Product }) {
  const { t } = useTranslation('explorer');
  const score = num(product, 'qualityScore', 'dataQualityScore', 'data_quality_score');
  const grade = (
    toStr(product.qualityGrade) ||
    toStr(product.dataQualityGrade) ||
    toStr(product.data_quality_grade)
  ).toLowerCase();

  const dimensions = [
    {
      label: t('products.dimCompleteness'),
      value: num(product, 'completenessScore', 'completeness_score'),
    },
    { label: t('products.dimValidity'), value: num(product, 'validityScore', 'validity_score') },
    {
      label: t('products.dimConsistency'),
      value: num(product, 'consistencyScore', 'consistency_score'),
    },
    { label: t('products.dimIntegrity'), value: num(product, 'integrityScore', 'integrity_score') },
    {
      label: t('products.dimUniqueness'),
      value: num(product, 'uniquenessScore', 'uniqueness_score'),
    },
    {
      label: t('products.dimTimeliness'),
      value: num(product, 'timelinessScore', 'timeliness_score'),
    },
  ];

  const critical = num(product, 'criticalIssues', 'critical_issues');
  const important = num(product, 'importantIssues', 'important_issues');
  const optional = num(product, 'optionalIssues', 'optional_issues');
  const total = num(product, 'totalIssues', 'total_issues');

  const barColor = (v: number) => (v >= 80 ? 'bg-success' : v >= 50 ? 'bg-warning' : 'bg-error');
  const scoreColorVar =
    score >= 80 ? 'var(--success)' : score >= 50 ? 'var(--warning)' : 'var(--error)';

  return (
    <Section title={t('products.tabQuality')}>
      <div className="bg-surface-stat-card flex items-center gap-6 rounded-2xl p-5">
        <div className="relative h-24 w-24 shrink-0">
          <div
            className="h-24 w-24 rounded-full"
            style={{
              background: `conic-gradient(${scoreColorVar} ${String(score)}%, var(--muted) ${String(score)}% 100%)`,
            }}
          />
          <div className="bg-card absolute inset-[7px] flex flex-col items-center justify-center rounded-full shadow-sm">
            <span className="text-xl leading-none font-bold tabular-nums">{score.toFixed(0)}</span>
            {grade && (
              <span className="text-muted-foreground mt-0.5 text-[10px] capitalize">{grade}</span>
            )}
          </div>
        </div>
        <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-2.5">
          {dimensions.map(({ label, value }) => (
            <div key={label} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-[11px]">{label}</span>
                <span className="text-[11px] font-semibold tabular-nums">{value.toFixed(0)}</span>
              </div>
              <div className="bg-background/60 h-1.5 w-full overflow-hidden rounded-full">
                <div
                  className={cn('h-full rounded-full', barColor(value))}
                  style={{ width: `${String(Math.min(100, value))}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Issue-count summary. */}
      <div className="mt-3 flex flex-wrap gap-2">
        {total === 0 ? (
          <span className="text-success text-xs font-medium">{t('products.noIssues')}</span>
        ) : (
          <>
            {critical > 0 && (
              <Badge
                variant="outline"
                className={cn('pointer-events-none', SEVERITY_STYLES.DISAPPROVED)}
              >
                {t('products.criticalCount', { count: critical })}
              </Badge>
            )}
            {important > 0 && (
              <Badge
                variant="outline"
                className={cn('pointer-events-none', SEVERITY_STYLES.DEMOTED)}
              >
                {t('products.importantCount', { count: important })}
              </Badge>
            )}
            {optional > 0 && (
              <Badge
                variant="outline"
                className={cn('pointer-events-none', SEVERITY_STYLES.NOT_IMPACTED)}
              >
                {optional} {t('products.tabIssues')}
              </Badge>
            )}
          </>
        )}
      </div>
    </Section>
  );
}

/** Deduped source-issues list (from the fetched detail record). */
function IssuesSection({ product, isLoading }: { product: Product; isLoading: boolean }) {
  const { t } = useTranslation('explorer');
  const raw = arr<SourceIssue>(product, 'sourceIssues', 'source_issues');
  const issues = raw.filter(
    (issue, idx, list) =>
      list.findIndex(
        (i) =>
          i.code === issue.code &&
          i.attribute === issue.attribute &&
          i.description === issue.description,
      ) === idx,
  );

  if (isLoading) {
    return (
      <Section title={t('products.tabIssues')}>
        <div className="space-y-2">
          {[1, 2].map((n) => (
            <Skeleton key={n} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      </Section>
    );
  }
  if (issues.length === 0) return null;

  return (
    <Section title={t('products.tabIssues')} count={issues.length}>
      <div className="space-y-1.5">
        {issues.map((issue, i) => (
          <div
            key={`${issue.code}-${String(i)}`}
            className="flex items-start gap-2.5 rounded-lg border px-3 py-2"
          >
            {issue.severity === 'DISAPPROVED' ? (
              <XCircle className="text-error mt-0.5 h-3.5 w-3.5 shrink-0" />
            ) : issue.severity === 'DEMOTED' ? (
              <AlertTriangle className="text-warning mt-0.5 h-3.5 w-3.5 shrink-0" />
            ) : (
              <Info className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium">{issue.description}</span>
                <Badge
                  variant="outline"
                  className={cn(
                    'pointer-events-none rounded-full px-1.5 py-0 text-[10px]',
                    SEVERITY_STYLES[issue.severity] ?? '',
                  )}
                >
                  {issue.severity}
                </Badge>
              </div>
              {issue.attribute && (
                <span className="text-muted-foreground mt-0.5 block font-mono text-[11px]">
                  {issue.attribute}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/** Per-destination approval breakdown (from the fetched detail record). */
function CountriesSection({ product, isLoading }: { product: Product; isLoading: boolean }) {
  const { t } = useTranslation('explorer');
  const destinations = arr<Destination>(product, 'sourceDestinations', 'source_destinations');

  if (isLoading) {
    return (
      <Section title={t('products.tabCountries')}>
        <div className="space-y-2">
          {[1, 2].map((n) => (
            <Skeleton key={n} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      </Section>
    );
  }
  if (destinations.length === 0) return null;

  return (
    <Section title={t('products.tabCountries')}>
      <div className="space-y-3">
        {destinations.map((dest, i) => {
          const context = dest.reportingContext ?? dest.reporting_context ?? 'Unknown';
          const approved = dest.approvedCountries ?? dest.approved_countries ?? [];
          const disapproved = dest.disapprovedCountries ?? dest.disapproved_countries ?? [];
          const pending = dest.pendingCountries ?? dest.pending_countries ?? [];
          return (
            <div
              key={`${context}-${String(i)}`}
              className="space-y-2 rounded-lg border px-3 py-2.5"
            >
              <span className="text-sm font-medium capitalize">
                {context.replace(/_/g, ' ').toLowerCase()}
              </span>
              <div className="flex flex-wrap gap-3 text-[11px]">
                {approved.length > 0 && (
                  <span className="text-success">
                    {t('products.approved')}: {approved.join(', ')}
                  </span>
                )}
                {disapproved.length > 0 && (
                  <span className="text-error">
                    {t('products.disapproved')}: {disapproved.join(', ')}
                  </span>
                )}
                {pending.length > 0 && (
                  <span className="text-warning">
                    {t('products.pending')}: {pending.join(', ')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

/**
 * Product Catalog detail panel — a right-side Sheet using the shared onboarding
 * panel design (gallery with thumbnails below, Highlights, Specifications,
 * Attributes, Description, Identifiers) plus the data-quality info the catalog
 * carries (scores, dimensions, issues, destinations). Fetches the full detail
 * record and merges it over the clicked row; steps prev/next through the page.
 */
export function CatalogDetailPanel({
  products,
  activeIndex,
  onClose,
  onNavigate,
  connectionId,
  connectorCode,
  subsidiaryId,
}: CatalogDetailPanelProps) {
  const { t } = useTranslation('explorer');
  const scrollRef = useRef<HTMLDivElement>(null);
  // Kept across prev/next so a reviewer can step through products' enrichments.
  const [tab, setTab] = useState<DetailTab>('details');

  const open = activeIndex !== null;
  const row = activeIndex !== null ? products[activeIndex] : undefined;

  // Retain the last product so the panel keeps its content while animating out.
  const [rendered, setRendered] = useState<Product | undefined>(undefined);
  if (row && row !== rendered) setRendered(row);
  const active = row ?? rendered;

  const externalId = active
    ? toStr(active.externalId) ||
      toStr(active.external_id) ||
      toStr(active.offerId) ||
      toStr(active.offer_id) ||
      toStr(active.id)
    : '';

  // Fetch the full record (issues + destinations aren't on the list row) and
  // shallow-merge it over the clicked row.
  const detailQuery = useProductDetail(
    connectionId,
    externalId,
    subsidiaryId,
    open && !!connectionId && !!externalId,
  );
  const merged = useMemo<Product>(() => {
    if (!active) return {};
    const fetched = detailQuery.data;
    if (!fetched) return active;
    const inner = (
      fetched.data && typeof fetched.data === 'object' ? fetched.data : fetched
    ) as Product;
    return { ...active, ...inner };
  }, [active, detailQuery.data]);

  const detail = useMemo(() => toProductDetail(merged), [merged]);

  // Reset scroll to top whenever the product changes.
  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: 0 });
  }, [activeIndex, open, tab]);

  const total = products.length;
  const hasPrev = activeIndex !== null && activeIndex > 0;
  const hasNext = activeIndex !== null && activeIndex < total - 1;
  const price = formatFeedPrice(detail.price);
  const title = detail.title || t('products.thisProduct');

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        aria-label={title}
        className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-xl"
      >
        {active && (
          <>
            {/* Sticky header */}
            <header className="border-border bg-background sticky top-0 z-10 border-b px-5 pt-5 pr-12 pb-4">
              <div className="flex items-start gap-3">
                <span aria-hidden="true" className="bg-muted shrink-0 overflow-hidden rounded-lg">
                  <CachedImage
                    src={detail.images[0] ?? ''}
                    alt=""
                    className="h-14 w-14 object-cover"
                  />
                </span>
                <div className="min-w-0 flex-1">
                  {detail.brand && (
                    <p className="text-muted-foreground truncate text-xs font-medium">
                      {detail.brand}
                    </p>
                  )}
                  <SheetTitle className="text-foreground text-base leading-snug font-semibold">
                    {title}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    {t('products.detailTabsLabel')}
                  </SheetDescription>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {price && (
                  <span className="text-foreground mr-1 text-base font-semibold">{price}</span>
                )}
                <AvailabilityBadge availability={detail.availability} size="lg" />
                {detail.link && (
                  <a
                    href={detail.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-primary/10 hover:bg-primary/15 inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
                  >
                    <span className="text-foreground underline">{t('products.viewListing')}</span>
                    <ExternalLink aria-hidden="true" className="text-primary h-4 w-4 shrink-0" />
                  </a>
                )}
              </div>
              <SegmentedTabs
                className="bg-selected-row mt-3"
                ariaLabel={t('products.detailTabsLabel')}
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'details', label: t('products.enrichment.tabDetails') },
                  { value: 'enrichments', label: t('products.enrichment.tabEnrichments') },
                ]}
              />
            </header>

            {/* Scrollable body */}
            <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
              {tab === 'enrichments' ? (
                <CatalogEnrichmentHistory
                  externalId={externalId}
                  connectorCode={connectorCode}
                  product={merged}
                  enabled={open}
                />
              ) : (
                <>
                  <div className="px-5 py-4">
                    <ProductDetailGallery
                      key={detail.id}
                      images={detail.images}
                      title={detail.title}
                    />
                  </div>
                  <HighlightsSection highlights={detail.productHighlights} />
                  <QualitySection product={merged} />
                  <SpecificationsSection product={detail} />
                  <AttributesSection product={detail} />
                  <DescriptionSection description={detail.description} />
                  <IssuesSection product={merged} isLoading={detailQuery.isLoading} />
                  <CountriesSection product={merged} isLoading={detailQuery.isLoading} />
                  <IdentifiersSection product={detail} />
                </>
              )}
            </div>

            {/* Sticky footer — row navigation */}
            <footer className="border-border bg-background sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t px-5 py-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!hasPrev}
                className={cn('cursor-pointer', !hasPrev && 'cursor-not-allowed')}
                onClick={() => {
                  if (activeIndex !== null) onNavigate(activeIndex - 1);
                }}
              >
                <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                {t('products.detailPrev')}
              </Button>
              <span className="text-muted-foreground text-sm tabular-nums">
                {t('products.detailPosition', { current: (activeIndex ?? 0) + 1, total })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!hasNext}
                className={cn('cursor-pointer', !hasNext && 'cursor-not-allowed')}
                onClick={() => {
                  if (activeIndex !== null) onNavigate(activeIndex + 1);
                }}
              >
                {t('products.detailNext')}
                <ChevronRight aria-hidden="true" className="h-4 w-4" />
              </Button>
            </footer>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
